import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import FollowUp from '../models/FollowUp';
import { ContactOption } from '../models/enums/ContactOption';
import { updateFollowUp } from '../services/follow-up-service';
import ApiResponse from '../models/ApiResponse';
import {getDateFromString, getDefaultTimeZone, getTimeZones} from "../utils/DateTimeUtils";

interface EditFollowUpModalProps {
    isOpen: boolean;
    onClose: () => void;
    onError: (message: string) => void;
    onUpdateFollowUp: (followUp: FollowUp) => void;
    followUp: FollowUp;
}

const EditFollowUpModal: React.FC<EditFollowUpModalProps> = ({
                                                                 isOpen,
                                                                 onClose,
                                                                 onError,
                                                                 onUpdateFollowUp,
                                                                 followUp
                                                             }) => {
    const today = new Date().toISOString().split('T')[0];
    const maxNotes = 1000;
    const contactOptions = Object.values(ContactOption).map(option => ({ value: option, label: option }));

    const [followUpDate, setFollowUpDate] = useState('');
    const [followUpTime, setFollowUpTime] = useState('');
    const [timeZone, setTimeZone] = useState(getDefaultTimeZone());
    const [contactOption, setContactOption] = useState(contactOptions[0].value || '');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFollowUpDate(followUp.followUpDate.toISOString().split('T')[0] || '');
            setContactOption(followUp.contactOption || contactOptions[0].value);
            setFollowUpTime((followUp.followUpTime || ''));
            setTimeZone(followUp.timeZone || getDefaultTimeZone());
            setNotes(followUp.notes || '');
            setErrors({});
        }
    }, [isOpen, followUp]);

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!followUpDate) {
            errs.followUpDate = 'Date is required';
        } else if (followUpDate < today) {
            errs.followUpDate = 'Date cannot be in the past';
        }
        if (followUpTime && !timeZone) {
            errs.timeZone = 'Time zone is required when a time is set';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        setLoading(true);
        if (!validate()) {
            setLoading(false);
            return;
        }

        const payload: FollowUp = {
            ...followUp,
            followUpDate: getDateFromString(followUpDate),
            followUpTime: followUpTime,
            timeZone: followUpTime ? timeZone : '',
            contactOption: contactOption as ContactOption,
            notes: notes.trim() || undefined
        };

        try {
            const response: ApiResponse<FollowUp> = await updateFollowUp(payload);
            if (response.status === 200 && response.data) {
                onUpdateFollowUp(response.data);
            } else {
                console.error('Invalid response when updating follow-up: ', response);
                onError('Unable to update follow-up. Please try again later');
            }
        } catch (err: any) {
            console.error('Error updating follow-up', err);
            onError('Unable to update follow-up. Please try again later');
        } finally {
            setLoading(false);
            onClose();
        }
    };

    const timeZones = getTimeZones();

    return (
        <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" aria-hidden="true" onClick={onClose} />

            <div className="relative bg-white w-full max-w-md p-6 rounded-lg shadow-lg overflow-auto max-h-screen">
                <Dialog.Title className="text-xl font-semibold mb-4">Edit Follow-Up</Dialog.Title>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="follow-up-date" className="block font-medium">Date *</label>
                        <input
                            id="follow-up-date"
                            type="date"
                            min={today}
                            value={followUpDate}
                            onChange={e => setFollowUpDate(e.target.value)}
                            className="w-full border rounded p-2"
                        />
                        {errors.followUpDate && <p className="text-red-600 text-sm">{errors.followUpDate}</p>}
                    </div>

                    {/* Time + Time Zone (optional) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label htmlFor="follow-up-time" className="block font-medium">Time</label>
                            <input
                                id="follow-up-time"
                                data-testid="follow-up-time"
                                type="time"
                                value={followUpTime}
                                onChange={e => setFollowUpTime(e.target.value)}
                                className="w-full border rounded p-2"
                            />
                        </div>

                        <div>
                            <label htmlFor="follow-up-tz" className="block font-medium">Time Zone</label>
                            <select
                                id="follow-up-tz"
                                data-testid="follow-up-tz"
                                value={timeZone}
                                onChange={e => setTimeZone(e.target.value)}
                                className="w-full border rounded p-2"
                            >
                                {timeZones.map(tz => (
                                    <option key={tz} value={tz}>{tz}</option>
                                ))}
                            </select>
                            {errors.timeZone && <p className="text-red-600 text-sm">{errors.timeZone}</p>}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="follow-up-contact-option" className="block font-medium">Contact Option *</label>
                        <select
                            id="follow-up-contact-option"
                            value={contactOption}
                            onChange={e => setContactOption(e.target.value as ContactOption)}
                            className="w-full border rounded p-2"
                        >
                            {contactOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="follow-up-notes" className="block font-medium">Notes</label>
                        <textarea
                            id="follow-up-notes"
                            rows={3}
                            maxLength={maxNotes}
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            className="w-full border rounded p-2"
                        />
                        <p className="text-gray-500 text-sm mt-1">
                            {notes.length}/{maxNotes} characters
                        </p>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                        <button onClick={onClose} className="px-4 py-2 rounded border">Cancel</button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="px-4 py-2 rounded bg-crumbblue text-white"
                            data-testid="save-btn"
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default EditFollowUpModal;